<template>
    <div class="loan-notes-component">
        <div class="notes__container">
            <div class="note__container" v-for="note in notes" :key="note.id">
                <div v-bind:class="['note', { 'note--right': note.owner_id === userName }]">
                    <h3 class="note--info">{{ note.displayName }} <span>{{ note.createdDisplay }}</span></h3>
                    <div v-bind:class="['bubble', { 'bubble-bottom-left' : note.owner_id !== userName }, { 'bubble-bottom-right' : note.owner_id === userName }]" v-html="note.note"></div>
                </div>
            </div>
            <div v-if="notesLoaded && notes.length === 0">
                <h3 class="notes--empty"><i>This loan has no file notes</i></h3>
            </div>
            <div ref="notesbottom" class="notes-bottom"></div>
        </div>

        <div class="add-note__container">
            <div class="editor__container">
                <k-editor :tools="tools"
                            ref="noteseditor"
                            :content-style="wizardView ? { height: '100px' } : { height: '150px' }"
                            :resizable="false"
                            v-on:focus="onEditorFocus"
                            v-on:loaded="assignSendButton"
                            :default-content="wizardView ? wizardDefaultEditorContent : defaultEditorContent"
                            :defaultEditMode="'div'" />
            </div>
        </div>

        <div class="conditions__loader-container" v-if="isLoading">
            <loader :size="'large'" :type="'infinite-spinner'"></loader>
        </div>

        <transition name="dialogfade">
          <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
            <p class="error-dialog__msg">{{ errorMsg }}</p>
          </k-dialog>
        </transition>        
    </div>
</template>

<script>
    import auth from '../authConfig.js';
    import apiService from '../api/api.service.js';

    import { Input } from '@progress/kendo-vue-inputs';
    import { Button } from '@progress/kendo-vue-buttons';
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Dialog, DialogActionsBar, Window } from '@progress/kendo-vue-dialogs';
    import { Editor } from "@progress/kendo-vue-editor";

    export default {
        props: ['defaultNote', 'loanId', 'showLoanNotes', 'wizardView'],
        emits: ['wizardnoteadded'],
        data() {
            return {
                defaultEditorContent: 'Add your note here!',
                errorAlert: false,
                errorMsg: '',
                errorTitle: '',
                isLoading: false,
                notes: [],
                notesLoaded: false,
                showErrorDialog: false,
                tools: [
                    ["Bold", "Italic", "Underline"],
                    ["Undo", "Redo"],
                    ["Link", "Unlink", "InsertImage", "ViewHtml"],
                    ["AlignLeft", "AlignCenter", "AlignRight"],
                    ["OrderedList", "UnorderedList", "Indent", "Outdent"],
                ],
                userAzureId: '',
                userName: '',
                users: [],
                wizardNotes: []           
            }
        },
        computed: {
            wizardDefaultEditorContent() {
                if (this.wizardView) {
                    return this.defaultNote;
                }

                return '';
            }
        },
        mounted() {
            this.openFileNotes();
        },
        created() {
        },
        watch: {
        },
        methods: {
            addLoanNote() {
                let noteHtml = this.$refs.noteseditor.getHTML();
                let noteText = noteHtml.replace(/<[^>]+>/g, '');

                if (noteText && noteText.trim() !== 'Add your note here!') {

                    let note = {
                        azure_id: this.userAzureId,
                        owner_id: this.userName,
                        loan_id: this.loanId,
                        message_type: 'UW',
                        note: noteHtml,
                        notesLoaded: false,
                        underwriter: '',
                        coord: '',
                        processor: '',
                        loan_officer: ''
                    };
                    
                    this.submitNote(note);

                    if (this.wizardView) {
                        this.$emit('wizardnoteadded', noteHtml);
                    }

                    this.$refs.noteseditor.setHTML(this.defaultEditorContent);
                }
            },
            assignSendButton() {
                let editor = this.$el.querySelector('.k-editor .k-toolbar');

                if (editor) {
                    // Check if button already exists to avoid duplicates
                    if (editor.querySelector('.add-note__btn')) {
                        return;
                    }

                    let button = document.createElement('button');
                    button.addEventListener('click', this.addLoanNote);
                    button.innerHTML += '<span class="k-icon k-i-flip-vertical"></span> Send';

                    button.classList.add('k-button', 'k-button-md', 'k-rounded-md', 'add-note__btn', 'k-button-solid', 'k-button-solid-base');
                    button.setAttribute('title', 'Send Note');
                    editor.appendChild(button);
                }
            },
            formatDateTime(stringDate) {
            if (stringDate) {
                return new Date(stringDate);
            }

            return '';
            },
            onEditorFocus() {
                let noteHtml = this.$refs.noteseditor.getHTML();
                let noteText = noteHtml.replace(/<[^>]+>/g, '');

                if (noteText && noteText.trim() === this.defaultEditorContent) {
                    this.$refs.noteseditor.setHTML('');
                }
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;
                this.errorAlert = true;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.toggleErrorDialog();
            },
            async openFileNotes() {
                this.isLoading = true;
                this.notesLoaded = false;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;
                
                this.userName = tokenResponse.account.username;
                this.userAzureId = tokenResponse.account.homeAccountId;

                apiService.GetLoanNotes(this.loanId, tokenResponse.accessToken)
                  .then(res => {
                    this.isLoading = false;
                    res.data.data.notes.forEach(note => {
                        note.created = this.formatDateTime(note.created);
                        note.createdDisplay = note.created.toLocaleDateString('en-US', { hour: 'numeric', minute: '2-digit'}).split(',').join('');

                        let author = res.data.data.users.find(user => user.email === note.owner_id);

                        note.displayName = author ? author.displayName : note.owner_id;
                    });

                    this.notes = res.data.data.notes.sort((a, b) => a.created - b.created);

                    this.users = res.data.data.users;

                    this.notesLoaded = true;

                    this.$nextTick(() => this.$refs.notesbottom?.scrollIntoView(true));                  
                  }).catch(error => {
                    console.log(error);
                    this.errorTitle = 'Failed to Fetch File Notes';

                    if(error.response?.data?.data) {
                        this.errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        this.errorMsg = '';
                    }

                    this.onRequestError(this.errorTitle, this.errorMsg);
                  });
            },
            async submitNote(note) {
                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                apiService.SubmitNote(note, tokenResponse.accessToken)
                  .then(res => {
                    this.openFileNotes();

                  }).catch(error => {
                    console.log(error);
                    this.errorTitle = 'Failed to Submit File Note';
                    
                    if(error.response?.data?.data) {
                        this.errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        this.errorMsg = '';
                    }

                    this.onRequestError(this.errorTitle, this.errorMsg);
                  });
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if (!this.showErrorDialog) {
                    this.errorAlert = false;
                    this.errorMsg = '';
                    this.errorTitle = '';
                }
            },
        },
        components: {
            'k-dialog': Dialog,
            'k-dialog-actions': DialogActionsBar,
            'k-button': Button,
            'loader': Loader,
            'k-input': Input,
            'k-editor': Editor
        }
    }    
</script>

<style>
    @import '/src/assets/css/loanNotesComponent.css';
</style>
